/**
 * RECOMMENDATION QUIZ  (traveller app)
 * ============================================================
 * Reads the quiz the admin console writes.
 *
 * Mirror of the provider app → src/lib/recommendation-quiz.ts
 *
 * WHY THIS EXISTS
 * ---------------
 * The quiz was a module-level array in ../data/recquizdata.js, so rewording a
 * question or adding an option needed a code change and a deploy. It now lives
 * at `app_config/recommendation_quiz`, edited at Admin → Recommendation Quiz
 * in the provider console.
 *
 * `hydrateQuestions` and `questionsForRoute` below must stay behaviourally
 * identical to the functions of the same name in the provider app. Both apps
 * read the same document; if they coerce it differently, the console shows an
 * admin a quiz that is not the one travellers are being asked.
 *
 * `app_config` is world-readable in firestore.rules, so this works for an
 * anonymous traveller.
 *
 * FALLBACK IS LOAD-BEARING
 * ------------------------
 * Every path falls back to DEFAULT_QUESTIONS. The quiz is the only way a
 * traveller creates an itinerary, so a missing, empty or unreachable document
 * must never mean "no quiz" — it means the questions that shipped.
 *
 * ANSWER KEYS ARE A CONTRACT
 * --------------------------
 * A question's `label` is the field its answer is written under on the
 * itinerary. `App/pages/Itinerary/index.js` reads `location`, `dates`,
 * `typeofstay`, `vacationtype` and `typeoftravelers` by those exact names, so
 * they are not free-form captions. The console says so where they are edited.
 * ============================================================
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DEFAULT_QUESTIONS, ROUTE_LABEL } from '../data/recquizdata';
import { getLocations } from './platformConfig';

export { ROUTE_LABEL };

export const QUIZ_DOC = { collection: 'app_config', id: 'recommendation_quiz' };

/**
 * The one route the calendar treats differently: it picks a single day rather
 * than a range, so `dates` lands on the itinerary as one timestamp instead of
 * a start/end pair. The itinerary page handles both and lays out one day for
 * the single-timestamp case.
 *
 * Matched by value, so renaming this route option in the console changes the
 * calendar back to a range. The console says so.
 */
export const SINGLE_DAY_ROUTE = 'Day Activity';

/**
 * Types whose answer key is decided here rather than by the admin.
 *
 * RecQuizForm writes the destination to `location` and the calendar to
 * `dates`, because those two names are read all over the itinerary page.
 */
const FIXED_LABELS = {
  locationselect: 'location',
  daterange: 'dates',
};

const QUESTION_TYPES = ['radiogroup', 'locationselect', 'daterange', 'multitagselect', 'textinput'];

const isFilledString = (v) => typeof v === 'string' && v.trim().length > 0;

/**
 * Coerce one stored entry into a question, or null if there is nothing usable.
 *
 * Tolerant on purpose. The bundled array wrote options as bare strings on the
 * destination step and `{value,label}` elsewhere, and the carousel list under
 * `image` rather than `images`. A document hand-edited into either shape has
 * to keep working — the alternative is a traveller looking at a quiz step with
 * no answers on it.
 */
function hydrateQuestion(raw, index) {
  if (!raw || typeof raw !== 'object') return null;

  const question = isFilledString(raw.question) ? raw.question.trim() : '';
  if (!question) return null;

  const type = QUESTION_TYPES.includes(raw.type) ? raw.type : 'radiogroup';

  const label = FIXED_LABELS[type] || (isFilledString(raw.label) ? raw.label.trim() : '');
  if (!label) return null;

  const options = (Array.isArray(raw.options) ? raw.options : [])
    .map((option) => {
      if (isFilledString(option)) return { value: option.trim(), label: option.trim() };
      if (option && typeof option === 'object') {
        const value = isFilledString(option.value) ? option.value.trim() : '';
        if (!value) return null;
        return { value, label: isFilledString(option.label) ? option.label.trim() : value };
      }
      return null;
    })
    .filter(Boolean);

  const rawImages = Array.isArray(raw.images) ? raw.images : Array.isArray(raw.image) ? raw.image : [];

  return {
    id: isFilledString(raw.id) ? raw.id.trim() : `${label}-${index}`,
    question,
    type,
    label,
    routes: (Array.isArray(raw.routes) ? raw.routes : []).filter(isFilledString).map((r) => r.trim()),
    images: rawImages.filter(isFilledString).map((i) => i.trim()),
    options,
    // Absent means enabled — a question that predates the flag is one the
    // admin is currently asking, and defaulting it off would silently shorten
    // the quiz.
    enabled: raw.enabled !== false,
  };
}

/**
 * Turn the raw document into the question list the quiz renders.
 *
 * The route question is forced to the front. It decides which of the others
 * are shown, so a route question sitting at step four would be asked after the
 * questions it is meant to filter — and it is forced onto every route, because
 * a route question a traveller can be routed past leaves the quiz with no
 * branch at all.
 */
export function hydrateQuestions(raw) {
  const list = raw && typeof raw === 'object' ? raw.questions : undefined;
  const hydrated = (Array.isArray(list) ? list : []).map(hydrateQuestion).filter(Boolean);

  if (hydrated.length === 0) return DEFAULT_QUESTIONS;

  const routeIndex = hydrated.findIndex((q) => q.label === ROUTE_LABEL);
  if (routeIndex > 0) {
    const [routeQuestion] = hydrated.splice(routeIndex, 1);
    hydrated.unshift(routeQuestion);
  }
  if (routeIndex >= 0) hydrated[0] = { ...hydrated[0], routes: [] };

  return hydrated;
}

/**
 * The questions asked on a given route, in order.
 *
 * A falsy route — the traveller has not answered the first question yet —
 * shows everything, so the quiz has a sensible length before the branch is
 * chosen and shortens once it is.
 */
export function questionsForRoute(questions, route) {
  return questions.filter(
    (q) => q.enabled !== false && (q.routes.length === 0 || !route || q.routes.includes(route))
  );
}

/**
 * Replace the destination step's options with the canonical destination list.
 *
 * The quiz used to carry its own hardcoded list of 18 places. The answer is
 * written to `itinerary.location` and then compared with `===` against
 * `listing.location`, so any option that is not a canonical destination cannot
 * match a listing — it produces an itinerary with no accommodation, no
 * restaurants and nothing to do, and fails silently while looking fine.
 *
 * That is exactly what had happened: only 8 of the 18 quiz options were real
 * destinations. Ten were provinces and cities no listing can carry — Midlands,
 * the Mashonalands, the Matabelelands, Gweru, Kwekwe, Chinhoyi — and thirteen
 * real destinations, Hwange and Mana Pools and Great Zimbabwe among them, could
 * not be reached from the quiz at all.
 *
 * So this list is NOT admin-editable, by design: it is derived from the same
 * `app_config` destinations the admin console already maintains, and the two
 * cannot drift apart again. Falls back to whatever the question carries if the
 * config cannot be read, because a destination step with no options is a dead
 * end for every traveller.
 */
export function withCanonicalDestinations(questions, locations) {
  const options = (Array.isArray(locations) ? locations : [])
    .map((l) => (typeof l === 'string' ? { value: l, label: l } : { value: l?.value, label: l?.label || l?.value }))
    .filter((o) => isFilledString(o.value));

  if (options.length === 0) return questions;

  return questions.map((q) => (q.type === 'locationselect' ? { ...q, options } : q));
}

/**
 * Cached for the session. The quiz changes about as often as an admin opens
 * the console, and re-reading it mid-quiz could change the questions under a
 * traveller who is halfway through answering them.
 */
let inflight = null;

export function getQuizQuestions() {
  if (!inflight) {
    // Both reads, together: the questions are useless without destinations that
    // can actually match a listing. `getLocations` has its own fallback, so a
    // rejection here can only come from the quiz read.
    inflight = Promise.all([
      getDoc(doc(db, QUIZ_DOC.collection, QUIZ_DOC.id))
        .then((snap) => hydrateQuestions(snap.exists() ? snap.data() : null))
        .catch((error) => {
          console.error('Failed to read app_config/recommendation_quiz; using the bundled questions.', error);
          return DEFAULT_QUESTIONS;
        }),
      getLocations().catch(() => []),
    ]).then(([questions, locations]) => withCanonicalDestinations(questions, locations));
  }
  return inflight;
}

/** Test seam — drops the cached read. */
export function resetQuizCache() {
  inflight = null;
}
